import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronLeft, Copy, Check, RefreshCw, Zap, Webhook, Users, Activity } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminTinypagesPage = () => {
  const [health, setHealth] = useState(null);
  const [webhookInfo, setWebhookInfo] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [h, w, e] = await Promise.all([
        axios.get(`${API}/tinypages/admin/health`, { withCredentials: true }),
        axios.get(`${API}/tinypages/admin/webhook-info`, { withCredentials: true }),
        axios.get(`${API}/tinypages/admin/events?limit=30`, { withCredentials: true }),
      ]);
      setHealth(h.data);
      setWebhookInfo(w.data);
      setEvents(e.data.events || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de chargement');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      toast.success('Copié dans le presse-papier');
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      toast.error('Erreur de copie');
    }
  };

  const resyncAll = async () => {
    if (!window.confirm('Envoyer TOUS les utilisateurs 10M vers TinyPages ? (opération en tâche de fond)')) return;
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/tinypages/admin/resync-all`, {}, { withCredentials: true });
      toast.success(`${res.data.queued} utilisateurs en cours de synchronisation`);
      setTimeout(fetchAll, 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-16">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="text-slate-600 hover:text-slate-900" data-testid="back-link">
              <ChevronLeft className="w-6 h-6" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                <Zap className="w-6 h-6 text-emerald-600" />
                Intégration TinyPages
              </h1>
              <p className="text-sm text-slate-500 mt-0.5">
                Sync bidirectionnelle entre 10M Investor et vos tunnels de vente TinyPages
              </p>
            </div>
          </div>
          <Button
            onClick={fetchAll}
            variant="outline"
            size="sm"
            data-testid="refresh-btn"
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Rafraîchir
          </Button>
        </div>

        {/* Status Card */}
        <Card className="mb-6" data-testid="health-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Activity className="w-4 h-4 text-emerald-600" />
              Statut de la connexion API TinyPages
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-sm text-slate-500">Chargement…</div>
            ) : health?.ok ? (
              <div className="space-y-2">
                <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                  ✓ Connecté
                </Badge>
                <div className="text-sm text-slate-600 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1 pt-2">
                  <div><span className="text-slate-400">Compte :</span> <strong>{health.profile?.name}</strong></div>
                  <div><span className="text-slate-400">Domaine :</span> {health.profile?.domain}</div>
                  <div><span className="text-slate-400">Locale :</span> {health.profile?.locale}</div>
                  <div><span className="text-slate-400">Devise :</span> {health.profile?.currency}</div>
                  <div><span className="text-slate-400">Reply-to :</span> {health.profile?.replyToEmail}</div>
                  <div><span className="text-slate-400">Timezone :</span> {health.profile?.timezone}</div>
                </div>
              </div>
            ) : (
              <div>
                <Badge className="bg-red-100 text-red-700">✗ Non connecté</Badge>
                <div className="text-sm text-red-600 mt-2">Raison : {health?.reason}</div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Outbound sync */}
        <Card className="mb-6" data-testid="outbound-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="w-4 h-4 text-blue-600" />
              Flux sortant : 10M Investor → TinyPages
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-600">
              Chaque nouvelle inscription/achat est automatiquement synchronisée dans TinyPages avec un tag correspondant.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
              <div className="bg-slate-100 rounded-lg p-3">
                <div className="font-semibold text-slate-700">Inscription gratuite</div>
                <code className="text-emerald-700 mt-1 block">lead-10m-gratuit</code>
              </div>
              <div className="bg-slate-100 rounded-lg p-3">
                <div className="font-semibold text-slate-700">Achat 1997€</div>
                <code className="text-blue-700 mt-1 block">membre-10m-payant</code>
              </div>
              <div className="bg-slate-100 rounded-lg p-3">
                <div className="font-semibold text-slate-700">Paiement 4x</div>
                <code className="text-purple-700 mt-1 block">paiement-4x-en-cours</code>
              </div>
            </div>
            <div className="border-t pt-4 flex items-center justify-between">
              <div>
                <div className="font-semibold text-sm text-slate-800">Synchroniser tous les utilisateurs existants</div>
                <div className="text-xs text-slate-500 mt-1">Envoie tous les comptes 10M actuels vers TinyPages en une fois</div>
              </div>
              <Button
                onClick={resyncAll}
                disabled={syncing || !health?.ok}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="resync-all-btn"
              >
                {syncing ? 'En cours…' : 'Tout resynchroniser'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Inbound webhook */}
        <Card className="mb-6" data-testid="inbound-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Webhook className="w-4 h-4 text-orange-600" />
              Flux entrant : TinyPages → 10M Investor
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-600">
              Configurez ce webhook dans TinyPages (via Zapier ou Make) pour créer automatiquement un compte 10M à chaque nouveau lead / achat.
            </p>

            {/* Webhook URL */}
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">URL du webhook</label>
              <div className="flex gap-2 mt-1">
                <code className="flex-1 bg-slate-900 text-emerald-400 rounded-md px-3 py-2 text-xs font-mono overflow-x-auto" data-testid="webhook-url">
                  {webhookInfo?.webhook_url || '—'}
                </code>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => copyToClipboard(webhookInfo?.webhook_url, 'url')}
                  data-testid="copy-url-btn"
                >
                  {copiedField === 'url' ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            {/* Header + Secret */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Header</label>
                <div className="flex gap-2 mt-1">
                  <code className="flex-1 bg-slate-100 rounded-md px-3 py-2 text-xs font-mono" data-testid="webhook-header">
                    {webhookInfo?.secret_header || '—'}
                  </code>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyToClipboard(webhookInfo?.secret_header, 'header')}
                  >
                    {copiedField === 'header' ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Valeur secrète</label>
                <div className="flex gap-2 mt-1">
                  <code className="flex-1 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-xs font-mono overflow-x-auto" data-testid="webhook-secret">
                    {webhookInfo?.secret_value || '—'}
                  </code>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyToClipboard(webhookInfo?.secret_value, 'secret')}
                    data-testid="copy-secret-btn"
                  >
                    {copiedField === 'secret' ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </div>

            {/* Payload example */}
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Format du payload attendu (JSON)</label>
              <pre className="mt-1 bg-slate-900 text-slate-200 rounded-md p-3 text-xs overflow-x-auto" data-testid="payload-example">
{JSON.stringify(webhookInfo?.expected_payload || {}, null, 2)}
              </pre>
              <p className="text-xs text-slate-500 mt-2">
                💡 <strong>event: &quot;lead&quot;</strong> → crée un compte gratuit. <strong>event: &quot;purchase&quot;</strong> → active l&apos;abonnement Club payant (1997€/12 mois).
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Events log */}
        <Card data-testid="events-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Événements récents ({events.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {events.length === 0 ? (
              <div className="text-sm text-slate-500 py-8 text-center">Aucun événement pour le moment.</div>
            ) : (
              <div className="divide-y divide-slate-100">
                {events.map((ev, i) => (
                  <div key={i} className="py-3 flex items-center justify-between text-sm" data-testid={`event-row-${i}`}>
                    <div className="flex items-center gap-3">
                      <Badge
                        className={
                          ev.type === 'inbound'
                            ? 'bg-orange-100 text-orange-700'
                            : ev.type === 'resync_all'
                            ? 'bg-purple-100 text-purple-700'
                            : 'bg-blue-100 text-blue-700'
                        }
                      >
                        {ev.type === 'inbound' ? '← Inbound' : ev.type === 'resync_all' ? '⟳ Resync' : '→ Outbound'}
                      </Badge>
                      <div>
                        <div className="font-medium text-slate-800">
                          {ev.email || (ev.type === 'resync_all' ? `${ev.success}/${ev.total} synchronisés` : '—')}
                        </div>
                        <div className="text-xs text-slate-500">
                          {ev.event && <span className="mr-2">event={ev.event}</span>}
                          {ev.action && <span className="mr-2">action={ev.action}</span>}
                          {ev.amount && <span>{ev.amount}€</span>}
                        </div>
                      </div>
                    </div>
                    <div className="text-xs text-slate-400">
                      {new Date(ev.created_at).toLocaleString('fr-FR')}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminTinypagesPage;
